import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasManagerAccess, isInventoryOnly, isInstallerOnly, canEditInventory } from '../App';
import {
  LayoutDashboard, DollarSign, Package, Calendar,
  Users, LogOut, Menu, Zap, TrendingUp, KanbanSquare, Plus, Wrench
} from 'lucide-react';

const myPayNav = { path: '/my-pay', label: 'My Pay', icon: DollarSign, roles: [] };

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'salesman', 'manager'] },
  { path: '/sales/new', label: 'New Sale', icon: Plus, roles: ['admin', 'salesman', 'manager'], highlight: true },
  { path: '/sales', label: 'Pipeline', icon: KanbanSquare, roles: ['admin', 'manager'], extraAccess: hasManagerAccess },
  { path: '/payments', label: 'Payments', icon: DollarSign, roles: ['admin', 'manager'], extraAccess: hasManagerAccess },
  { path: '/commissions', label: 'Commissions', icon: TrendingUp, roles: ['admin', 'salesman', 'manager'] },
  { path: '/inventory', label: 'Inventory', icon: Package, roles: ['admin', 'manager'], extraAccess: canEditInventory },
  { path: '/schedule', label: 'Schedule', icon: Calendar, roles: ['admin', 'manager'], extraAccess: hasManagerAccess },
  { path: '/service', label: 'Service', icon: Wrench, roles: ['admin', 'manager'], extraAccess: hasManagerAccess },
  { path: '/users', label: 'Team', icon: Users, roles: ['admin', 'manager'] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Narrow sidebars for single-purpose users
  const filtered = isInventoryOnly(user)
    ? [navItems.find(n => n.path === '/inventory'), myPayNav].filter(Boolean)
    : isInstallerOnly(user)
    ? [myPayNav]
    : navItems.filter(n =>
        n.roles.includes(user?.role) || (n.extraAccess && n.extraAccess(user))
      );

  return (
    <div className="min-h-screen flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-nova-950 text-white transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-5 border-b border-nova-800">
          <div className="flex items-center gap-2">
            <Zap className="w-7 h-7 text-nova-400" />
            <h1 className="text-xl font-bold">Nova Lights</h1>
          </div>
          <p className="text-nova-400 text-xs mt-1">{user?.full_name} &middot; {user?.role}</p>
        </div>
        <nav className="p-3 flex-1">
          {filtered.map(item => {
            const active = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-sm transition-colors ${
                  item.highlight && !active
                    ? 'bg-nova-600 text-white hover:bg-nova-500'
                    : active ? 'bg-nova-700 text-white' : 'text-nova-300 hover:bg-nova-800 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-nova-800">
          <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 text-nova-300 hover:text-white text-sm w-full rounded-lg hover:bg-nova-800">
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b px-4 py-3 flex items-center gap-3 lg:hidden">
          <button onClick={() => setSidebarOpen(true)} className="p-1">
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-nova-600" />
            <span className="font-semibold">Nova Lights</span>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
